import GrupperingPicker from 'components/GrupperingPicker';
import { IEnhetsgruppeNode } from 'components/GrupperingPicker/types';
import { IcebergButton, IcebergIcon } from '@tradesolution/iceberg-ui-react';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import EnhetsgruppeMedlemsskapsApi from 'services/KjederegisteretAdminApi/EnhetsgruppeMedlemsskapsApi';
import { EnhetgruppeKjedeMedlemskapDto } from 'services/KjederegisteretAdminApi/EnhetsgruppeMedlemsskapsApi/types';
import KjedeApi from 'services/KjederegisteretAdminApi/KjedeApi';
import {
  FlyttKjedeTilNyttInnkjopCommand,
  GetKjedeByIdResponse,
} from 'services/KjederegisteretAdminApi/KjedeApi/types';
import useToaster from 'utils/hooks/useToaster';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';
import HttpErrorHelper from 'utils/HttpErrorHelper';

/**
 * Props for the InnkjopsMedlemsskap component.
 */
interface Props {
  /**
   * Hvilken kjede ønsker vi å endre innkjøpssamarbeid for
   */
  kjede: GetKjedeByIdResponse;
}

const InnkjopsMedlemsskap = (props: Props) => {
  const toaster = useToaster();
  const [medlemsskap, setMedlemsskap] = useState<EnhetgruppeKjedeMedlemskapDto[]>([]);
  const [showNew, setShowNew] = useState<boolean>(false);
  const [showExtraInfo, setShowExtraInfo] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const loadMedlemsskap = async () => {
    const medlemmer = await EnhetsgruppeMedlemsskapsApi.getByKjedeId(props.kjede.kjedeID);
    setMedlemsskap(medlemmer);
    if (medlemmer.length > 0) {
      setCommand({ ...command, fraEnhetgruppeId: medlemmer[0].enhetgruppeingId });
    }
  };

  const defaultCommand: FlyttKjedeTilNyttInnkjopCommand = {
    fraEnhetgruppeId: -1,
    tilEnhetgruppeId: -1,
    fraOgMedDato: new Date(),
    kopierMedlemsnummer: false,
  };

  useEffect(() => {
    loadMedlemsskap();
    setCommand(defaultCommand);
  }, [props.kjede]);

  const formatDate = (date: Date | undefined) => {
    if (date) {
      return format(date, 'dd.MM.yyyy');
    }
    return '';
  };

  const [command, setCommand] = useState<FlyttKjedeTilNyttInnkjopCommand>(defaultCommand);

  const handleGrupperingSelected = (grupperinger: IEnhetsgruppeNode[]) => {
    setCommand({ ...command, tilEnhetgruppeId: grupperinger[0].id });
  };

  const handleDateSelected = (date: Date) => {
    setCommand({ ...command, fraOgMedDato: date });
  };

  const toggleKopierMedlemsnummer = () => {
    setCommand({ ...command, kopierMedlemsnummer: !command.kopierMedlemsnummer });
  };

  const submit = async () => {
    setLoading(true);
    try {
      await KjedeApi.flyttKjedeTilNyttInnkjop(props.kjede.kjedeID, command);
      toaster.success(
        "'Kjede flyttet til nytt innkjøpssamarbeid",
        `Kjede '${props.kjede.kjedenavn}' ble flyttet til nytt innkjøpssamarbeid.`,
      );
    } catch (err) {
      toaster.error(
        'Feil ved flytting av kjede til nytt innkjøpssamarbeid',
        HttpErrorHelper.formatError(err),
      );
    }
    setLoading(false);
  };

  return (
    <>
      <h6>Medlemsskap i innkjøpssamarbeid for {props.kjede.kjedenavn}</h6>
      <Table>
        <thead></thead>
        <tbody>
          {medlemsskap.map((m) => (
            <tr key={m.enhetgruppeingId}>
              <td>
                {formatDate(m.fraOgMedDato)} - {formatDate(m.tilOgMedDato)}
              </td>
              <td>
                {m.enhetgrupperingNavn} ({m.enhetgrupperingNr})
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Button onClick={() => setShowNew(true)}>Flytt kjede inn i nytt innkjøpssamarbeid</Button>
      <Modal show={showNew} onHide={() => setShowNew(false)}>
        <Modal.Header>
          Flytt {props.kjede.kjedenavn} fra eksisterende og inn i nytt innkjøpssamarbeid
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>
                Eksisterende innkjøpssamarbeid <span className="text-danger">*</span>
              </Form.Label>
              <GrupperingPicker
                disabled={true}
                treeSelectMode={'radioSelect'}
                findPreselectedNodesByID={() => [command.fraEnhetgruppeId]}
                onSelectionChanged={handleGrupperingSelected}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>
                Nytt innkjøpssamarbeid <span className="text-danger">*</span>
              </Form.Label>
              <GrupperingPicker
                closeOnSelect={true}
                enhetgrupperingType="IS"
                onlyTopLevel={true}
                treeSelectMode={'radioSelect'}
                onSelectionChanged={handleGrupperingSelected}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Dato for innmelding inn i nytt innkjøpssamarbeid</Form.Label>
              <NorwegianDatepicker
                onChange={handleDateSelected}
                value={command.fraOgMedDato}
                showFirstDaySuggestions
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Check
                type="checkbox"
                label="Kopier medlemsnummer"
                checked={command.kopierMedlemsnummer}
                onChange={toggleKopierMedlemsnummer}
              />
            </Form.Group>
          </Form>
          <p style={{ margin: '1rem 0' }}>
            <strong>
              <IcebergIcon icon="info" />{' '}
              <a href="#" onClick={() => setShowExtraInfo(!showExtraInfo)}>
                Mer info om denne jobben
              </a>
            </strong>
          </p>
          {showExtraInfo && (
            <Alert variant="info">
              <h6> Ved flytting inn i nytt innkjøpssamarbeid skjer følgende:</h6>
              <ul>
                <li>
                  En ny undergruppe med kjedens navn og nr vil opprettes under nytt
                  innkjøpssamarbeid
                </li>
                <li>
                  Aktive enhetsmedlemmer vil meldes ut fra undergruppe i nåværende innkjøpssamarbeid
                  og meldes inn som medlemmer i undergruppen opprettet under nytt
                  innkjøpssamarbeid{' '}
                </li>
                <li>
                  Fremtidige enhetsmedlemmer vil fjernes fra gammel undergruppe og kopieres inn i ny
                </li>
                <li>Gammel undergruppe vil bli markert som nedlagt.</li>
              </ul>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <IcebergButton
            type="button"
            variant="outline-primary"
            icon="close"
            className="col"
            onClick={() => setShowNew(false)}
          >
            Lukk
          </IcebergButton>
          <IcebergButton
            type="button"
            variant="primary"
            className="col"
            icon="send"
            onClick={submit}
            disabled={loading || command.fraEnhetgruppeId <= 0 || command.tilEnhetgruppeId <= 0}
          >
            Utfør
          </IcebergButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InnkjopsMedlemsskap;
